/**
 * Demo Events Data
 *
 * Pre-built temporal events for demonstration.
 * These events cluster the demo images into meaningful work moments.
 */

import type { TemporalEvent, ProofTrail, ProofStep, MemorySummary, MemoryAnchor } from '@/types/memory';

/**
 * Demo events for the 1970 Chevelle SS (v-001)
 * Craig @ Fast Cars - Complete restoration timeline
 */
export const chevelleEvents: TemporalEvent[] = [
  {
    id: 'evt-v-001-001',
    vehicleId: 'v-001',
    type: 'inspection',
    title: 'LS6 454 Engine - Initial Assessment',
    description: 'Documented as-received condition of the numbers-matching LS6 big block. Original engine showing typical wear patterns.',
    startTime: '2024-03-15T10:30:00Z',
    duration: 2,
    components: ['engine'],
    componentDetails: ['LS6 454 big block'],
    phase: 'initial-assessment',
    imageIds: ['img-001-001'],
    primaryImageId: 'img-001-001',
    imageCount: 1,
    confidence: 94,
    autoGenerated: false,
    intensity: 'light',
    relatedEventIds: [],
    isAnchor: true,
    anchorLabel: 'Project Start',
    createdAt: '2024-03-15T12:00:00Z',
    updatedAt: '2024-03-15T12:00:00Z',
  },
  {
    id: 'evt-v-001-002',
    vehicleId: 'v-001',
    type: 'discovery',
    title: 'Rear Quarter Panel - Rust Discovery',
    description: 'Significant rust damage found on driver side rear quarter panel during initial inspection. Will require metal work.',
    startTime: '2024-03-15T14:00:00Z',
    duration: 1,
    components: ['body'],
    componentDetails: ['rear quarter panel'],
    phase: 'initial-assessment',
    imageIds: ['img-001-006'],
    primaryImageId: 'img-001-006',
    imageCount: 1,
    confidence: 92,
    autoGenerated: false,
    intensity: 'light',
    previousEventId: 'evt-v-001-001',
    relatedEventIds: [],
    isAnchor: true,
    anchorLabel: 'Rust Discovery',
    createdAt: '2024-03-15T15:00:00Z',
    updatedAt: '2024-03-15T15:00:00Z',
  },
  {
    id: 'evt-v-001-003',
    vehicleId: 'v-001',
    type: 'inspection',
    title: 'Front Suspension - Assessment',
    startTime: '2024-03-16T11:00:00Z',
    duration: 1,
    components: ['suspension'],
    componentDetails: ['front A-arms and spindles'],
    phase: 'initial-assessment',
    imageIds: ['img-001-004'],
    primaryImageId: 'img-001-004',
    imageCount: 1,
    confidence: 89,
    autoGenerated: true,
    intensity: 'light',
    previousEventId: 'evt-v-001-002',
    relatedEventIds: [],
    isAnchor: false,
    createdAt: '2024-03-16T12:00:00Z',
    updatedAt: '2024-03-16T12:00:00Z',
  },
  {
    id: 'evt-v-001-004',
    vehicleId: 'v-001',
    type: 'inspection',
    title: 'Dashboard - Interior Assessment',
    startTime: '2024-03-17T09:00:00Z',
    duration: 2,
    components: ['interior'],
    componentDetails: ['dashboard and gauges'],
    phase: 'initial-assessment',
    imageIds: ['img-001-009'],
    primaryImageId: 'img-001-009',
    imageCount: 1,
    confidence: 88,
    autoGenerated: true,
    intensity: 'light',
    previousEventId: 'evt-v-001-003',
    relatedEventIds: [],
    isAnchor: false,
    createdAt: '2024-03-17T11:00:00Z',
    updatedAt: '2024-03-17T11:00:00Z',
  },
  {
    id: 'evt-v-001-005',
    vehicleId: 'v-001',
    type: 'teardown',
    title: 'Carburetor - Disassembly',
    startTime: '2024-04-02T09:00:00Z',
    duration: 3,
    components: ['fuel'],
    componentDetails: ['Holley 780 carburetor'],
    phase: 'teardown',
    imageIds: ['img-001-003'],
    primaryImageId: 'img-001-003',
    imageCount: 1,
    confidence: 91,
    autoGenerated: true,
    intensity: 'moderate',
    previousEventId: 'evt-v-001-004',
    relatedEventIds: [],
    isAnchor: false,
    createdAt: '2024-04-02T12:00:00Z',
    updatedAt: '2024-04-02T12:00:00Z',
  },
  {
    id: 'evt-v-001-006',
    vehicleId: 'v-001',
    type: 'fabrication',
    title: 'Quarter Panel - Metal Work',
    description: 'Rust repair on driver side quarter panel. Cut out damaged sections and welded in new metal.',
    startTime: '2024-06-25T08:00:00Z',
    endTime: '2024-06-25T16:00:00Z',
    duration: 8,
    components: ['body'],
    componentDetails: ['rear quarter panel'],
    phase: 'restoration',
    imageIds: ['img-001-007'],
    primaryImageId: 'img-001-007',
    imageCount: 1,
    confidence: 96,
    autoGenerated: false,
    intensity: 'heavy',
    previousEventId: 'evt-v-001-005',
    relatedEventIds: [],
    isAnchor: true,
    anchorLabel: 'Major Metal Work',
    createdAt: '2024-06-25T17:00:00Z',
    updatedAt: '2024-06-25T17:00:00Z',
  },
  {
    id: 'evt-v-001-007',
    vehicleId: 'v-001',
    type: 'installation',
    title: 'Front Suspension - Assembly',
    startTime: '2024-08-10T14:00:00Z',
    endTime: '2024-08-10T17:00:00Z',
    duration: 3,
    components: ['suspension'],
    componentDetails: ['front A-arms and spindles'],
    phase: 'assembly',
    imageIds: ['img-001-005'],
    primaryImageId: 'img-001-005',
    imageCount: 1,
    confidence: 95,
    autoGenerated: true,
    intensity: 'moderate',
    previousEventId: 'evt-v-001-006',
    relatedEventIds: [],
    isAnchor: false,
    createdAt: '2024-08-10T18:00:00Z',
    updatedAt: '2024-08-10T18:00:00Z',
  },
  {
    id: 'evt-v-001-008',
    vehicleId: 'v-001',
    type: 'installation',
    title: 'LS6 454 Engine - Rebuild Complete',
    description: 'Numbers-matching LS6 454 fully rebuilt and detailed. Ready for installation.',
    startTime: '2024-09-20T10:00:00Z',
    endTime: '2024-09-20T16:00:00Z',
    duration: 6,
    components: ['engine'],
    componentDetails: ['LS6 454 big block'],
    phase: 'assembly',
    imageIds: ['img-001-002'],
    primaryImageId: 'img-001-002',
    imageCount: 1,
    confidence: 97,
    autoGenerated: false,
    intensity: 'heavy',
    previousEventId: 'evt-v-001-007',
    relatedEventIds: [],
    isAnchor: true,
    anchorLabel: 'Engine Complete',
    createdAt: '2024-09-20T17:00:00Z',
    updatedAt: '2024-09-20T17:00:00Z',
  },
  {
    id: 'evt-v-001-009',
    vehicleId: 'v-001',
    type: 'installation',
    title: 'Exhaust Headers - Installation',
    startTime: '2024-09-25T12:00:00Z',
    duration: 2,
    components: ['exhaust'],
    componentDetails: ['long tube headers'],
    phase: 'assembly',
    imageIds: ['img-001-012'],
    primaryImageId: 'img-001-012',
    imageCount: 1,
    confidence: 93,
    autoGenerated: true,
    intensity: 'moderate',
    previousEventId: 'evt-v-001-008',
    relatedEventIds: [],
    isAnchor: false,
    createdAt: '2024-09-25T14:00:00Z',
    updatedAt: '2024-09-25T14:00:00Z',
  },
  {
    id: 'evt-v-001-010',
    vehicleId: 'v-001',
    type: 'finishing',
    title: 'Body - Paint Complete',
    description: 'Full body painted in Cranberry Red. Base/clear with wet sanding and buffing.',
    startTime: '2024-10-05T08:00:00Z',
    endTime: '2024-10-05T18:00:00Z',
    duration: 10,
    components: ['body'],
    componentDetails: ['full body exterior'],
    phase: 'finishing',
    imageIds: ['img-001-008'],
    primaryImageId: 'img-001-008',
    imageCount: 1,
    confidence: 98,
    autoGenerated: false,
    intensity: 'heavy',
    previousEventId: 'evt-v-001-009',
    relatedEventIds: [],
    isAnchor: true,
    anchorLabel: 'Paint Complete',
    createdAt: '2024-10-05T19:00:00Z',
    updatedAt: '2024-10-05T19:00:00Z',
  },
  {
    id: 'evt-v-001-011',
    vehicleId: 'v-001',
    type: 'finishing',
    title: 'SS Wheels - Final Install',
    startTime: '2024-10-20T13:00:00Z',
    duration: 2,
    components: ['wheels'],
    componentDetails: ['SS 5-spoke wheels 15x7'],
    phase: 'finishing',
    imageIds: ['img-001-011'],
    primaryImageId: 'img-001-011',
    imageCount: 1,
    confidence: 96,
    autoGenerated: true,
    intensity: 'light',
    previousEventId: 'evt-v-001-010',
    relatedEventIds: [],
    isAnchor: false,
    createdAt: '2024-10-20T15:00:00Z',
    updatedAt: '2024-10-20T15:00:00Z',
  },
  {
    id: 'evt-v-001-012',
    vehicleId: 'v-001',
    type: 'finishing',
    title: 'Dashboard - Restoration Complete',
    startTime: '2024-11-01T10:00:00Z',
    duration: 3,
    components: ['interior'],
    componentDetails: ['dashboard and gauges'],
    phase: 'assembly',
    imageIds: ['img-001-010'],
    primaryImageId: 'img-001-010',
    imageCount: 1,
    confidence: 94,
    autoGenerated: true,
    intensity: 'moderate',
    previousEventId: 'evt-v-001-011',
    relatedEventIds: [],
    isAnchor: false,
    createdAt: '2024-11-01T13:00:00Z',
    updatedAt: '2024-11-01T13:00:00Z',
  },
];

/**
 * Demo proof trails
 */
export const demoProofTrails: ProofTrail[] = [
  {
    id: 'proof-v-001-quarter-panel',
    vehicleId: 'v-001',
    title: 'Rear Quarter Panel Replacement',
    component: 'rear quarter panel',
    componentCategory: 'body',
    steps: [
      {
        id: 'step-0',
        order: 0,
        label: 'Before',
        description: 'Original rusted rear quarter panel - driver side',
        condition: 'as-received',
        eventId: 'evt-v-001-002',
        imageIds: ['img-001-006'],
        primaryImageId: 'img-001-006',
        timestamp: '2024-03-15T14:00:00Z',
        hasEvidence: true,
        confidence: 92,
      },
      {
        id: 'step-1',
        order: 1,
        label: 'Fabrication/Repair',
        description: 'Metal work - rust cut out, new metal welded',
        condition: 'repaired',
        eventId: 'evt-v-001-006',
        imageIds: ['img-001-007'],
        primaryImageId: 'img-001-007',
        timestamp: '2024-06-25T16:00:00Z',
        hasEvidence: true,
        confidence: 96,
      },
      {
        id: 'step-2',
        order: 2,
        label: 'Final',
        description: 'Painted and finished in Cranberry Red',
        condition: 'painted',
        eventId: 'evt-v-001-010',
        imageIds: ['img-001-008'],
        primaryImageId: 'img-001-008',
        timestamp: '2024-10-05T18:00:00Z',
        hasEvidence: true,
        confidence: 98,
      },
    ],
    isComplete: true,
    confidence: 95,
    totalImages: 3,
    dateRange: {
      start: '2024-03-15T14:00:00Z',
      end: '2024-10-05T18:00:00Z',
    },
    exportable: true,
    createdAt: '2024-10-06T00:00:00Z',
    updatedAt: '2024-10-06T00:00:00Z',
  },
  {
    id: 'proof-v-001-engine',
    vehicleId: 'v-001',
    title: 'LS6 454 Engine Restoration',
    component: 'LS6 454 big block',
    componentCategory: 'engine',
    steps: [
      {
        id: 'step-0',
        order: 0,
        label: 'Before',
        description: 'Numbers-matching LS6 as received',
        condition: 'as-received',
        eventId: 'evt-v-001-001',
        imageIds: ['img-001-001'],
        primaryImageId: 'img-001-001',
        timestamp: '2024-03-15T10:30:00Z',
        hasEvidence: true,
        confidence: 94,
      },
      {
        id: 'step-1',
        order: 1,
        label: 'Final',
        description: 'Fully rebuilt and detailed engine',
        condition: 'restored',
        eventId: 'evt-v-001-008',
        imageIds: ['img-001-002'],
        primaryImageId: 'img-001-002',
        timestamp: '2024-09-20T16:00:00Z',
        hasEvidence: true,
        confidence: 97,
      },
    ],
    isComplete: true,
    confidence: 96,
    totalImages: 2,
    dateRange: {
      start: '2024-03-15T10:30:00Z',
      end: '2024-09-20T16:00:00Z',
    },
    exportable: true,
    createdAt: '2024-09-21T00:00:00Z',
    updatedAt: '2024-09-21T00:00:00Z',
  },
  {
    id: 'proof-v-001-suspension',
    vehicleId: 'v-001',
    title: 'Front Suspension Rebuild',
    component: 'front A-arms and spindles',
    componentCategory: 'suspension',
    steps: [
      {
        id: 'step-0',
        order: 0,
        label: 'Before',
        description: 'Worn suspension with original ball joints',
        condition: 'as-received',
        eventId: 'evt-v-001-003',
        imageIds: ['img-001-004'],
        primaryImageId: 'img-001-004',
        timestamp: '2024-03-16T11:00:00Z',
        hasEvidence: true,
        confidence: 89,
      },
      {
        id: 'step-1',
        order: 1,
        label: 'Installation',
        description: 'New ball joints, powder coated A-arms installed',
        condition: 'installed',
        eventId: 'evt-v-001-007',
        imageIds: ['img-001-005'],
        primaryImageId: 'img-001-005',
        timestamp: '2024-08-10T17:00:00Z',
        hasEvidence: true,
        confidence: 95,
      },
    ],
    isComplete: true,
    confidence: 92,
    totalImages: 2,
    dateRange: {
      start: '2024-03-16T11:00:00Z',
      end: '2024-08-10T17:00:00Z',
    },
    exportable: true,
    createdAt: '2024-08-11T00:00:00Z',
    updatedAt: '2024-08-11T00:00:00Z',
  },
];

/**
 * Demo memory summaries
 */
export const demoSummaries: MemorySummary[] = [
  {
    id: 'summary-2024-03',
    vehicleId: 'v-001',
    periodStart: '2024-03-01T00:00:00Z',
    periodEnd: '2024-03-31T23:59:59Z',
    periodLabel: 'March 2024',
    title: 'Initial Assessment & Rust Discovery',
    narrative: 'March focused on comprehensive documentation of the as-received 1970 Chevelle SS. Notable discoveries included significant rust on the driver side rear quarter panel and worn front suspension components. The numbers-matching LS6 454 was documented and assessed for rebuild.',
    bulletPoints: [
      'Complete vehicle documentation',
      'Rust discovered on rear quarter panel',
      'Suspension wear identified',
      'Engine assessed for rebuild',
    ],
    eventCount: 4,
    eventIds: ['evt-v-001-001', 'evt-v-001-002', 'evt-v-001-003', 'evt-v-001-004'],
    primaryComponents: ['engine', 'body', 'suspension', 'interior'],
    phases: ['initial-assessment'],
    heroImageIds: ['img-001-001', 'img-001-006'],
    intensity: 'moderate',
    imageCount: 4,
    autoGenerated: true,
    createdAt: '2024-04-01T00:00:00Z',
  },
  {
    id: 'summary-2024-06',
    vehicleId: 'v-001',
    periodStart: '2024-06-01T00:00:00Z',
    periodEnd: '2024-06-30T23:59:59Z',
    periodLabel: 'June 2024',
    title: 'Body Metal Work',
    narrative: 'June was focused on major metal work for the body restoration. The rusted rear quarter panel was cut out and new metal was expertly welded in place. This was a critical milestone in the restoration.',
    bulletPoints: [
      'Quarter panel rust repair',
      'Welding and metal fabrication',
      'Body prep for paint',
    ],
    eventCount: 1,
    eventIds: ['evt-v-001-006'],
    primaryComponents: ['body'],
    phases: ['restoration'],
    heroImageIds: ['img-001-007'],
    intensity: 'heavy',
    imageCount: 1,
    autoGenerated: true,
    createdAt: '2024-07-01T00:00:00Z',
  },
  {
    id: 'summary-2024-09',
    vehicleId: 'v-001',
    periodStart: '2024-09-01T00:00:00Z',
    periodEnd: '2024-09-30T23:59:59Z',
    periodLabel: 'September 2024',
    title: 'Engine & Exhaust Assembly',
    narrative: 'September marked major assembly milestones. The numbers-matching LS6 454 was fully rebuilt and detailed to show quality. Long tube ceramic-coated headers were installed to complete the exhaust system.',
    bulletPoints: [
      'LS6 454 rebuild complete',
      'Engine detailed to show quality',
      'Exhaust headers installed',
    ],
    eventCount: 2,
    eventIds: ['evt-v-001-008', 'evt-v-001-009'],
    primaryComponents: ['engine', 'exhaust'],
    phases: ['assembly'],
    heroImageIds: ['img-001-002'],
    intensity: 'heavy',
    imageCount: 2,
    autoGenerated: true,
    createdAt: '2024-10-01T00:00:00Z',
  },
  {
    id: 'summary-2024-10',
    vehicleId: 'v-001',
    periodStart: '2024-10-01T00:00:00Z',
    periodEnd: '2024-10-31T23:59:59Z',
    periodLabel: 'October 2024',
    title: 'Paint & Finishing',
    narrative: 'October saw the completion of bodywork with the car being painted in stunning Cranberry Red. Base/clear coat was applied, wet sanded, and buffed to perfection. SS wheels with Redline tires were installed.',
    bulletPoints: [
      'Full body painted Cranberry Red',
      'SS wheels installed',
      'Exterior finishing complete',
    ],
    eventCount: 2,
    eventIds: ['evt-v-001-010', 'evt-v-001-011'],
    primaryComponents: ['body', 'wheels'],
    phases: ['finishing'],
    heroImageIds: ['img-001-008'],
    intensity: 'heavy',
    imageCount: 2,
    autoGenerated: true,
    createdAt: '2024-11-01T00:00:00Z',
  },
];

/**
 * Demo memory anchors (human-defined important moments)
 */
export const demoAnchors: MemoryAnchor[] = [
  {
    id: 'anchor-001',
    vehicleId: 'v-001',
    eventId: 'evt-v-001-001',
    label: 'Project Start',
    description: 'First day documenting Craig\'s Chevelle',
    timestamp: '2024-03-15T10:30:00Z',
    anchorType: 'milestone',
    imageIds: ['img-001-001'],
    primaryImageId: 'img-001-001',
    createdBy: 'Craig',
    createdAt: '2024-03-15T12:00:00Z',
  },
  {
    id: 'anchor-002',
    vehicleId: 'v-001',
    eventId: 'evt-v-001-002',
    label: 'Rust Discovery',
    description: 'Found significant rust in quarter panel',
    timestamp: '2024-03-15T14:00:00Z',
    anchorType: 'discovery',
    imageIds: ['img-001-006'],
    primaryImageId: 'img-001-006',
    createdBy: 'Craig',
    createdAt: '2024-03-15T15:00:00Z',
  },
  {
    id: 'anchor-003',
    vehicleId: 'v-001',
    eventId: 'evt-v-001-008',
    label: 'Engine Complete',
    description: 'LS6 454 rebuild finished',
    timestamp: '2024-09-20T16:00:00Z',
    anchorType: 'completion',
    imageIds: ['img-001-002'],
    primaryImageId: 'img-001-002',
    createdBy: 'Craig',
    createdAt: '2024-09-20T17:00:00Z',
  },
  {
    id: 'anchor-004',
    vehicleId: 'v-001',
    eventId: 'evt-v-001-010',
    label: 'Paint Complete',
    description: 'Cranberry Red paint finished',
    timestamp: '2024-10-05T18:00:00Z',
    anchorType: 'milestone',
    imageIds: ['img-001-008'],
    primaryImageId: 'img-001-008',
    createdBy: 'Craig',
    createdAt: '2024-10-05T19:00:00Z',
  },
];

/**
 * All demo events combined
 */
export const demoEvents: TemporalEvent[] = [...chevelleEvents];

/**
 * Get events by vehicle ID
 */
export function getEventsByVehicle(vehicleId: string): TemporalEvent[] {
  return demoEvents.filter(e => e.vehicleId === vehicleId);
}

/**
 * Get proof trails by vehicle ID
 */
export function getProofTrailsByVehicle(vehicleId: string): ProofTrail[] {
  return demoProofTrails.filter(p => p.vehicleId === vehicleId);
}

/**
 * Get summaries by vehicle ID
 */
export function getSummariesByVehicle(vehicleId: string): MemorySummary[] {
  return demoSummaries.filter(s => s.vehicleId === vehicleId);
}

/**
 * Get anchors by vehicle ID
 */
export function getAnchorsByVehicle(vehicleId: string): MemoryAnchor[] {
  return demoAnchors.filter(a => a.vehicleId === vehicleId);
}

/**
 * Find proof trail for a component
 */
export function findProofTrail(
  vehicleId: string,
  componentSearch: string
): ProofTrail | undefined {
  const search = componentSearch.toLowerCase();
  return demoProofTrails.find(p =>
    p.vehicleId === vehicleId &&
    (p.component.toLowerCase().includes(search) ||
     p.title.toLowerCase().includes(search))
  );
}

/**
 * Get events in a date range
 */
export function getEventsInRange(
  vehicleId: string,
  start: string,
  end: string
): TemporalEvent[] {
  const startDate = new Date(start);
  const endDate = new Date(end);

  return demoEvents.filter(e => {
    if (e.vehicleId !== vehicleId) return false;
    const eventDate = new Date(e.startTime);
    return eventDate >= startDate && eventDate <= endDate;
  });
}
